import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import CodexDocList from '../components/codex-admin/CodexDocList'
import CodexEditorForm from '../components/codex-admin/CodexEditorForm'
import CodexPreviewPane from '../components/codex-admin/CodexPreviewPane'
import {
  CODEX_TEMPLATES,
  createCodexDraft,
  deleteCodexDraft,
  getCodexDoc,
  hardDeleteCodexDoc,
  listCodexAdminDocs,
  listCodexRevisions,
  installStarterCodexEntries,
  publishCodexDoc,
  rollbackCodexDoc,
  saveCodexDraft,
  setCodexPublicPreview,
  unpublishCodexDoc,
  validateDraftForPublish
} from '../services/codexService'
import { DEFAULT_CODEX_LINK_MAPPINGS, getCodexLinkMappings, mergeTagMappingsFromEntries, saveCodexLinkMappings } from '../services/codexLinkMappingsService'
import { setCodexDynamicMappings } from '../utils/codexContextLinks'
import './CodexAdmin.css'

function CodexAdmin() {
  const { user, userData, isAdminTeacher } = useAuth()
  const navigate = useNavigate()
  const [docs, setDocs] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [draft, setDraft] = useState(null)
  const [etag, setEtag] = useState(null)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [revisions, setRevisions] = useState([])
  const [hasLocalChanges, setHasLocalChanges] = useState(false)
  const [mappingsText, setMappingsText] = useState('')
  const [linkMappings, setLinkMappings] = useState({ byTag: {}, byComposer: {}, byGenre: {}, byStyle: {} })
  const [mappingType, setMappingType] = useState('byTag')
  const [mappingKey, setMappingKey] = useState('')
  const [mappingEntryId, setMappingEntryId] = useState('')
  const [mappingSectionId, setMappingSectionId] = useState('')
  const [publishAsVitrine, setPublishAsVitrine] = useState(false)

  const isAllowed = userData?.role === 'teacher' && isAdminTeacher
  const selectedDoc = useMemo(() => docs.find((d) => d.id === selectedId) || null, [docs, selectedId])
  const validationErrors = useMemo(() => validateDraftForPublish(draft), [draft])
  const publishedDocs = useMemo(() => docs.filter((d) => d.status === 'published'), [docs])
  const mappingRows = useMemo(() => {
    const rows = []
    ;['byTag', 'byComposer', 'byGenre', 'byStyle'].forEach((type) => {
      const map = linkMappings?.[type] || {}
      Object.entries(map).forEach(([key, value]) => {
        if (!value?.codexEntryId) return
        rows.push({
          type,
          key,
          codexEntryId: value.codexEntryId,
          sectionId: value.sectionId || ''
        })
      })
    })
    return rows
  }, [linkMappings])

  const typeLabels = {
    byTag: 'Tag',
    byComposer: 'Compositeur',
    byGenre: 'Genre',
    byStyle: 'Style'
  }

  useEffect(() => {
    if (!isAllowed) return
    listCodexAdminDocs({ includeArchived: true }).then(setDocs).catch(() => setDocs([]))
    getCodexLinkMappings()
      .then((m) => {
        setLinkMappings(m)
        setMappingsText(JSON.stringify(m, null, 2))
      })
      .catch(() => {
        const empty = { byTag: {}, byComposer: {}, byGenre: {}, byStyle: {} }
        setLinkMappings({ byTag: {}, byComposer: {}, byGenre: {}, byStyle: {} })
        setMappingsText(JSON.stringify(empty, null, 2))
      })
  }, [isAllowed])

  useEffect(() => {
    if (!selectedId) return
    getCodexDoc(selectedId).then((d) => {
      if (!d) return
      setDraft(d.draft || {
        title: d.published?.title || '',
        shortDescription: d.published?.shortDescription || '',
        category: d.published?.category || 'parcours',
        order: d.published?.order ?? 999,
        relatedNodeIds: d.published?.relatedNodeIds || [],
        relatedTags: d.published?.relatedTags || [],
        contentBlocks: d.published?.contentBlocks || []
      })
      setEtag(d.draft?.etag || null)
      setHasLocalChanges(false)
    }).catch(() => {})
    listCodexRevisions(selectedId).then(setRevisions).catch(() => setRevisions([]))
  }, [selectedId])

  const refreshDocs = async () => {
    const data = await listCodexAdminDocs({ includeArchived: true })
    setDocs(data)
  }

  useEffect(() => {
    if (!selectedId || !draft || !hasLocalChanges) return undefined
    const timer = setTimeout(async () => {
      try {
        await saveCodexDraft(selectedId, { ...draft, expectedEtag: etag }, user)
        const updated = await getCodexDoc(selectedId)
        setDraft(updated?.draft || draft)
        setEtag(updated?.draft?.etag || null)
        setMessage('Brouillon enregistré automatiquement')
        setHasLocalChanges(false)
      } catch (e) {
        setMessage(e.message || 'Erreur autosave')
      }
    }, 1000)
    return () => clearTimeout(timer)
  }, [selectedId, draft, hasLocalChanges, etag, user])

  const saveMappingsObject = async (nextMappings) => {
    await saveCodexLinkMappings(nextMappings, user)
    setCodexDynamicMappings(nextMappings)
    setLinkMappings(nextMappings)
    setMappingsText(JSON.stringify(nextMappings, null, 2))
  }

  const normalizeUiKey = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')

  const handleAddMapping = async () => {
    const key = normalizeUiKey(mappingKey)
    if (!key) {
      setMessage('Cle manquante')
      return
    }
    if (!mappingEntryId) {
      setMessage('Fiche cible manquante')
      return
    }
    const nextMappings = {
      ...linkMappings,
      [mappingType]: {
        ...(linkMappings?.[mappingType] || {}),
        [key]: {
          codexEntryId: mappingEntryId,
          ...(mappingSectionId.trim() ? { sectionId: mappingSectionId.trim() } : {})
        }
      }
    }
    await saveMappingsObject(nextMappings)
    setMessage('Mapping ajoute')
    setMappingKey('')
    setMappingSectionId('')
  }

  const handleDeleteMapping = async (type, key) => {
    const map = { ...(linkMappings?.[type] || {}) }
    delete map[key]
    const nextMappings = {
      ...linkMappings,
      [type]: map
    }
    await saveMappingsObject(nextMappings)
    setMessage('Mapping supprime')
  }

  if (!isAllowed) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-empty">
          <h2>Accès réservé</h2>
          <p>Cette page est réservée aux professeurs admin autorisés.</p>
          <button type="button" className="dashboard-signin-btn" onClick={() => navigate('/dashboard')}>
            Retour au dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="codex-admin-page">
      <header className="codex-admin-header">
        <div className="codex-admin-header-title-row">
          <h1>Codex Admin</h1>
          <button
            type="button"
            className="codex-admin-back-btn"
            onClick={() => navigate('/dashboard')}
          >
            Retour dashboard prof
          </button>
        </div>
        <div className="codex-admin-actions">
          <button
            type="button"
            onClick={async () => {
              try {
                const result = await installStarterCodexEntries(user, { publish: true, overwrite: false })
                await refreshDocs()
                const currentMappings = await getCodexLinkMappings()
                const mergedMappings = mergeTagMappingsFromEntries(currentMappings, result.entries)
                await saveMappingsObject(mergedMappings)
                setMessage(`Starter installe: ${result.created} creees, ${result.updated} mises a jour, ${result.skipped} deja presentes`)
              } catch (e) {
                setMessage(e.message || 'Impossible d installer les fiches prefaites')
              }
            }}
          >
            Installer fiches prefaites + tags
          </button>
          <button
            type="button"
            onClick={async () => {
              const id = await createCodexDraft({ title: 'Nouvelle fiche' }, user)
              await refreshDocs()
              setSelectedId(id)
              setMessage('Nouveau brouillon créé')
            }}
          >
            Nouvelle fiche
          </button>
          <button
            type="button"
            disabled={!selectedId || saving}
            onClick={async () => {
              setSaving(true)
              setMessage('')
              try {
                await saveCodexDraft(selectedId, { ...draft, expectedEtag: etag }, user)
                const updated = await getCodexDoc(selectedId)
                setDraft(updated?.draft || draft)
                setEtag(updated?.draft?.etag || null)
                setMessage('Brouillon enregistré')
                setHasLocalChanges(false)
                await refreshDocs()
              } catch (e) {
                setMessage(e.message || 'Erreur sauvegarde')
              } finally {
                setSaving(false)
              }
            }}
          >
            Enregistrer brouillon
          </button>
          <label className="codex-admin-vitrine-check">
            <input
              type="checkbox"
              checked={publishAsVitrine}
              onChange={(e) => setPublishAsVitrine(e.target.checked)}
            />
            Publier comme fiche vitrine (beta)
          </label>
          {selectedDoc?.status === 'published' && (
            <label className="codex-admin-vitrine-check">
              <input
                type="checkbox"
                checked={!!selectedDoc.publicPreview}
                onChange={async (e) => {
                  if (!selectedId || !user) return
                  try {
                    await setCodexPublicPreview(selectedId, e.target.checked, user)
                    setMessage(e.target.checked ? 'Fiche marquée vitrine' : 'Vitrine retirée')
                    const list = await listCodexAdminDocs({ includeArchived: true })
                    setDocs(list)
                  } catch (err) {
                    setMessage(err.message || 'Erreur')
                  }
                }}
              />
              Fiche vitrine (visible sans compte autorisé en beta)
            </label>
          )}
          <button
            type="button"
            disabled={!selectedId || validationErrors.length > 0}
            onClick={async () => {
              try {
                await publishCodexDoc(selectedId, user, { publicPreview: publishAsVitrine })
                setPublishAsVitrine(false)
                setMessage('Publication réussie')
                await refreshDocs()
                setRevisions(await listCodexRevisions(selectedId))
              } catch (e) {
                setMessage(e.message || 'Erreur publication')
              }
            }}
          >
            Publier
          </button>
          <button
            type="button"
            disabled={!selectedId}
            onClick={async () => {
              try {
                await unpublishCodexDoc(selectedId, user)
                setMessage('Document repassé en brouillon')
                await refreshDocs()
              } catch (e) {
                setMessage(e.message || 'Erreur')
              }
            }}
          >
            Dépublier
          </button>
          <button
            type="button"
            disabled={!selectedId}
            onClick={async () => {
              if (!selectedId) return
              const ok = window.confirm('Archiver cette fiche ? Elle sera masquée par défaut mais accessible dans la section Archivées.')
              if (!ok) return
              try {
                await deleteCodexDraft(selectedId, user)
                setSelectedId(null)
                setDraft(null)
                setEtag(null)
                setRevisions([])
                await refreshDocs()
                setMessage('Fiche archivée')
              } catch (e) {
                setMessage(e.message || 'Erreur suppression')
              }
            }}
          >
            Archiver fiche
          </button>
          <button
            type="button"
            className="codex-admin-danger-btn"
            disabled={!selectedId}
            onClick={async () => {
              if (!selectedId) return
              const ok = window.confirm('Suppression DEFINITIVE ? Cette action est irreversible et supprime document, brouillon et revisions.')
              if (!ok) return
              try {
                await hardDeleteCodexDoc(selectedId, user)
                setSelectedId(null)
                setDraft(null)
                setEtag(null)
                setRevisions([])
                await refreshDocs()
                setMessage('Fiche supprimee definitivement')
              } catch (e) {
                setMessage(e.message || 'Erreur suppression definitive')
              }
            }}
          >
            Supprimer definitivement
          </button>
        </div>
      </header>

      {message ? <p className="codex-admin-message">{message}</p> : null}

      <div className="codex-admin-layout">
        <CodexDocList docs={docs} selectedId={selectedId} onSelect={setSelectedId} />
        <CodexEditorForm
          draft={draft}
          validationErrors={validationErrors}
          onChange={(key, value) => {
            setDraft((prev) => ({ ...(prev || {}), [key]: value }))
            setHasLocalChanges(true)
          }}
          onAddBlock={(type) => {
            setDraft((prev) => ({ ...(prev || {}), contentBlocks: [...(prev?.contentBlocks || []), { type, text: '' }] }))
            setHasLocalChanges(true)
          }}
          onDeleteBlock={(idx) => {
            setDraft((prev) => ({ ...(prev || {}), contentBlocks: (prev?.contentBlocks || []).filter((_, i) => i !== idx) }))
            setHasLocalChanges(true)
          }}
          onTemplate={(templateId) => {
            const t = CODEX_TEMPLATES.find((x) => x.id === templateId)
            if (!t) return
            setDraft((prev) => ({ ...(prev || {}), contentBlocks: t.blocks }))
            setHasLocalChanges(true)
          }}
        />
        <CodexPreviewPane draft={draft} />
      </div>

      {selectedId && revisions.length > 0 && (
        <section className="codex-admin-revisions">
          <h3>Historique publications</h3>
          <ul>
            {revisions.map((r) => (
              <li key={r.id}>
                <span>Version {r.version}</span>
                <button
                  type="button"
                  onClick={async () => {
                    await rollbackCodexDoc(selectedId, r.id, user)
                    setMessage(`Rollback vers version ${r.version}`)
                    await refreshDocs()
                  }}
                >
                  Restaurer
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="codex-admin-mappings">
        <h3>Mappings de liens contextuels (sans code)</h3>
        <div className="codex-admin-mapping-builder">
          <div className="codex-admin-mapping-builder-grid">
            <label>
              Type
              <select value={mappingType} onChange={(e) => setMappingType(e.target.value)}>
                <option value="byTag">Tag</option>
                <option value="byComposer">Compositeur</option>
                <option value="byGenre">Genre</option>
                <option value="byStyle">Style</option>
              </select>
            </label>
            <label>
              Cle source
              <input
                value={mappingKey}
                onChange={(e) => setMappingKey(e.target.value)}
                placeholder="ex: cadenceparfaite / mozart / romantique"
              />
            </label>
            <label>
              Fiche Codex cible
              <select value={mappingEntryId} onChange={(e) => setMappingEntryId(e.target.value)}>
                <option value="">Selectionner...</option>
                {publishedDocs.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.published?.title || d.slug || d.id}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Section (optionnel)
              <input
                value={mappingSectionId}
                onChange={(e) => setMappingSectionId(e.target.value)}
                placeholder="ex: cadence-parfaite"
              />
            </label>
          </div>
          <div className="codex-admin-template-row">
            <button type="button" onClick={handleAddMapping}>Ajouter ce mapping</button>
          </div>
        </div>
        {mappingRows.length > 0 && (
          <div className="codex-admin-mapping-list">
            <h4>Mappings actifs</h4>
            <ul>
              {mappingRows.map((row) => (
                <li key={`${row.type}:${row.key}`}>
                  <span>
                    {typeLabels[row.type]}: <strong>{row.key}</strong> → <code>{row.codexEntryId}</code>
                    {row.sectionId ? ` (section: ${row.sectionId})` : ''}
                  </span>
                  <button type="button" onClick={() => handleDeleteMapping(row.type, row.key)}>Supprimer</button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <p>
          Associe tags/compositeurs/genres/styles a des fiches Codex publiees.
          Format: {"{ byTag, byComposer, byGenre, byStyle }"} avec cles en minuscules.
        </p>
        <textarea
          value={mappingsText}
          onChange={(e) => setMappingsText(e.target.value)}
          rows={16}
          spellCheck={false}
        />
        <div className="codex-admin-template-row">
          <button
            type="button"
            onClick={() => {
              setMappingsText(JSON.stringify(DEFAULT_CODEX_LINK_MAPPINGS, null, 2))
              setMessage('Template de mappings charge')
            }}
          >
            Charger template par defaut
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                await saveCodexLinkMappings(DEFAULT_CODEX_LINK_MAPPINGS, user)
                setCodexDynamicMappings(DEFAULT_CODEX_LINK_MAPPINGS)
                setMappingsText(JSON.stringify(DEFAULT_CODEX_LINK_MAPPINGS, null, 2))
                setMessage('Template de mappings installe en base')
              } catch {
                setMessage('Impossible d installer le template')
              }
            }}
          >
            Installer template par defaut
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                const parsed = JSON.parse(mappingsText || '{}')
                await saveCodexLinkMappings(parsed, user)
                setCodexDynamicMappings(parsed)
                setMessage('Mappings enregistres')
              } catch {
                setMessage('JSON invalide pour les mappings')
              }
            }}
          >
            Enregistrer les mappings
          </button>
          <button
            type="button"
            onClick={async () => {
              const m = await getCodexLinkMappings()
              setMappingsText(JSON.stringify(m, null, 2))
              setMessage('Mappings recharges')
            }}
          >
            Recharger depuis la base
          </button>
        </div>
      </section>
    </div>
  )
}

export default CodexAdmin
